// Typed wrapper around `QueryClient.invalidateQueries` for the
// billing domain. Mirrors `invalidateVisibility` — accepts ONLY
// prefix-shaped query keys so callers can't accidentally pass a
// filter-included key to `invalidateQueries` (the silent-cache-miss
// regression from issue #890).
//
// Today the billing slice only has `subscriptionKeys.detail(orgId)`
// which is already at prefix depth (no filter object), so the
// `detailPrefix(orgId)` factory returns the same shape. We keep the
// wrapper + the `*Prefix` factory in place for two reasons:
//
//   1. Symmetry with the visibility domain — every domain that
//      participates in TanStack invalidation goes through a typed
//      wrapper, so reviewers can spot direct `qc.invalidateQueries`
//      calls in PRs as a smell.
//   2. Forward-compatibility — if billing later grows filters
//      (per-cycle history, per-engine plans, etc.) we already have
//      the prefix-vs-exact branding in place.

import type { QueryClient } from "@tanstack/react-query";
import type { PrefixQueryKey } from "./queryKeys";

/**
 * Invalidate a billing-domain query slice by prefix key. Only the
 * prefix-shaped factories (`subscriptionKeys.*Prefix(...)` and the
 * filter-less slice factories that already return prefix-depth keys)
 * are accepted — passing a filter-included key is a compile error.
 *
 * Returns the same `Promise<void>` that `queryClient.invalidateQueries`
 * returns, so callers can `await` it when ordering matters (e.g.
 * when chaining with a subsequent refetch after a successful
 * checkout completes).
 */
export function invalidateBilling(
  queryClient: QueryClient,
  prefixKey: PrefixQueryKey,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: prefixKey });
}
